import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Play } from "lucide-react";
import type { SoundResponse, BoardWithSoundsResponse } from "@wizard-sounds/shared";
import { useAudio } from "@/components/audio-provider";
import { NowPlayingBar } from "@/components/now-playing-bar";
import { useKeyboard } from "@/hooks/use-keyboard";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

function EqBars() {
  return (
    <span className="flex items-end gap-[3px]">
      {[0, 1, 2, 3].map((i) => (
        <motion.span
          key={i}
          className="inline-block w-[3px] rounded-full bg-ink"
          animate={{ height: ["4px", "14px", "6px", "12px", "4px"] }}
          transition={{
            duration: 0.8 + i * 0.1,
            repeat: Infinity,
            delay: i * 0.12,
            ease: "easeInOut",
          }}
        />
      ))}
    </span>
  );
}

export function BoardMode() {
  const [boards, setBoards] = useState<BoardWithSoundsResponse[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const { play, isPlaying, stopAll } = useAudio();

  useEffect(() => {
    api
      .boards()
      .then((res) => setBoards(res.boards))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const sounds = boards[activeTab]?.sounds ?? [];

  const handlePlay = useCallback(
    (sound: SoundResponse) => {
      play(sound.id, sound.audio_url, {
        title: sound.title,
        durationMs: sound.duration_ms,
        slug: sound.slug,
      });
    },
    [play],
  );

  useKeyboard(sounds, handlePlay, stopAll);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-spark border-t-transparent" />
      </div>
    );
  }

  const mobileRows = Math.ceil(sounds.length / 4);

  return (
    <div className="flex h-screen flex-col">
      {/* Header — flex-1 on left/right keeps tabs centered when NowPlayingBar appears */}
      <div className="flex items-center border-b border-mist px-3 py-2 sm:px-4">
        <div className="flex min-w-0 flex-1">
          <Link
            to="/"
            className="flex shrink-0 items-center gap-1.5 text-sm text-fog transition-colors hover:text-parchment"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Exit board</span>
          </Link>
        </div>

        {boards.length > 1 && (
          <div className="flex shrink-0 items-center gap-1 rounded-full bg-slate-raised p-1">
            {boards.map((board, i) => (
              <button
                key={board.id}
                onClick={() => setActiveTab(i)}
                className={cn(
                  "relative rounded-full px-4 py-1.5 font-mono text-[10px] tracking-wider transition-colors sm:text-[11px]",
                  activeTab === i ? "text-ink" : "text-fog hover:text-parchment",
                )}
              >
                {activeTab === i && (
                  <motion.div
                    layoutId="board-tab"
                    className="absolute inset-0 rounded-full bg-spark"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative z-10">{board.name}</span>
              </button>
            ))}
          </div>
        )}

        <div className="flex min-w-0 flex-1 items-center justify-end">
          <NowPlayingBar />
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-hidden p-2 sm:p-3">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="grid h-full gap-2 sm:gap-2.5"
            style={{
              gridTemplateColumns: "repeat(4, 1fr)",
              gridTemplateRows: `repeat(${mobileRows}, 1fr)`,
            }}
          >
            {sounds.map((sound, i) => {
              const playing = isPlaying(sound.id);
              return (
                <motion.button
                  key={sound.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.25, delay: i * 0.02 }}
                  onClick={() => handlePlay(sound)}
                  whileTap={{ scale: 0.94 }}
                  className={cn(
                    "group relative flex flex-col items-center justify-center rounded-2xl border-2 p-2 text-center transition-all duration-300 sm:rounded-[20px] sm:p-3",
                    playing
                      ? "border-spark bg-gradient-to-b from-spark/20 to-spark/5 shadow-[0_0_32px_-4px_rgba(255,182,73,0.35)]"
                      : "border-mist bg-slate hover:border-spark/40 hover:bg-slate-raised hover:shadow-[0_0_24px_-4px_rgba(255,182,73,0.12)]",
                  )}
                >
                  {/* Animated ring when playing */}
                  {playing && (
                    <motion.div
                      className="absolute inset-0 rounded-2xl border-2 border-spark/40 sm:rounded-[20px]"
                      animate={{ opacity: [0.4, 0, 0.4] }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />
                  )}

                  {/* Play button / EQ bars */}
                  <div
                    className={cn(
                      "mb-1.5 flex h-8 w-8 items-center justify-center rounded-full transition-all duration-200 sm:mb-2 sm:h-10 sm:w-10",
                      playing
                        ? "bg-spark shadow-[0_0_16px_rgba(255,182,73,0.4)]"
                        : "bg-spark/15 group-hover:bg-spark/25",
                    )}
                  >
                    {playing ? (
                      <EqBars />
                    ) : (
                      <Play className="ml-0.5 h-3.5 w-3.5 text-spark sm:h-4 sm:w-4" />
                    )}
                  </div>

                  {/* Title */}
                  <span
                    className={cn(
                      "line-clamp-2 text-[11px] font-medium leading-tight transition-colors sm:text-xs",
                      playing ? "text-spark" : "text-parchment",
                    )}
                  >
                    {sound.title}
                  </span>

                  {/* Keyboard shortcut badge */}
                  {i < 9 && (
                    <span
                      className={cn(
                        "absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded font-mono text-[9px] transition-colors sm:h-5 sm:w-5 sm:text-[10px]",
                        playing ? "bg-spark/20 text-spark" : "bg-mist/50 text-fog",
                      )}
                    >
                      {i + 1}
                    </span>
                  )}
                </motion.button>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
